import styles from './CreateUpfrontPaymentDialog.module.css';

import React, { useMemo, useState } from 'react';

import Dialog from 'components/ui/Dialog';
import useTranslation from 'components/hooks/useTranslation';
import BngButton, { Variant } from 'components/bng/ui/BngButton';
import UiBlocker from 'components/bng/ui/UiBlocker';
import UiMsg from 'components/ui/UiMsg';
import Api from 'components/Api';
import BngRadio from 'components/bng/form/BngRadio';
import { proposalUtils } from 'components/service/bng/AccountApi';
import useAsyncEffect from 'components/hooks/useAsyncEffect';

export default function CreateUpfrontPaymentDialog({
  closeModal = _.noop,
  accountId,
  invoices = [],
  onUpdate = _.noop,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [upfrontPaymentMonths, setUpfrontPaymentMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState();
  const thisMonth = new Date(new Date().setDate(1));
  const futureInvoices = useMemo(() => {
    return invoices
      .filter((i) => i.invoiceProjection.referenceMonth >= thisMonth && i.invoiceProjection.status === 'ACTIVE')
      .sort((a, b) => a.invoiceProjection.referenceMonth - b.invoiceProjection.referenceMonth);
  }, [invoices]);

  useAsyncEffect({
    onMount: async () => {
      try {
        setLoading(true);
        const fetchedUpfrontPaymentMonths = await Api.Account.fetchUpfrontPaymentMonths({ accountId });
        const sortedMonths = fetchedUpfrontPaymentMonths
          .sort((a, b) => b.months - a.months)
          .filter((month) => month.months <= futureInvoices.length);
        setSelectedMonth(sortedMonths[0].id);
        setUpfrontPaymentMonths(sortedMonths);
      } catch (e) {
        console.error('Error on function fetchUpfrontPaymentMonths()', e);
        UiMsg.error(t('upfront.payment.month.fetch.error'));
      } finally {
        setLoading(false);
      }
    },
  });

  const selectedProjections = useMemo(() => {
    const monthApplied = upfrontPaymentMonths.find((month) => month.id === selectedMonth);
    if (!monthApplied) {
      return [];
    }
    const projections = [];

    const months = new Set();

    for (const futureInvoice of futureInvoices) {
      if(months.size === monthApplied.months) {
        break;
      }
      projections.push(futureInvoice);

      const data = futureInvoice.invoiceProjection.referenceMonth;
      const key = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
      if (!months.has(key)) {
        months.add(key);
      }
    }
    return projections;
  }, [selectedMonth]);

  const { total, fullPricing } = useMemo(() => {
    const monthApplied = upfrontPaymentMonths.find((month) => month.id === selectedMonth);
    if (!monthApplied) {
      return 0;
    }

    const monthDiscount = monthApplied.discount / 100;
    const currency = futureInvoices[0]?.invoiceProjection?.contract.currency.symbol;

    const nominalPricing = selectedProjections.reduce((acc, i) => acc + i.invoiceProjection.nominalPricing, 0);
    const discount = nominalPricing * monthDiscount;
    return {
      total: proposalUtils.formatPricing(nominalPricing - discount, currency),
      fullPricing: proposalUtils.formatPricing(nominalPricing, currency),
    };
  }, [selectedProjections, selectedMonth]);

  return (
    <Dialog
      className="CreateUpfrontPaymentDialog"
      title={t('upfront.payments')}
      onClose={closeModal}
      newDialogLayout={true}
    >
      <Dialog.Body className={styles.upfrontPaymentDialogBody}>
        <UiBlocker block={loading}>
          <section className={styles.monthsSection}>
            <span className={styles.titleSpan}>{t('TIME')}</span>
            <div className={styles.monthsWrapper}>
              {upfrontPaymentMonths.map((upfrontPaymentMonth, idx) => {
                const selected = selectedMonth === upfrontPaymentMonth.id;
                return (
                  <div
                    className={`${styles.upfrontPaymentMonth} ${selected ? styles.selectedRadio : ''}`}
                    key={`month-${idx}`}
                    onClick={() => setSelectedMonth(upfrontPaymentMonth.id)}
                  >
                    <BngRadio
                      name={'month'}
                      label={t('months.amount', [upfrontPaymentMonth.months])}
                      checked={selected}
                      field={{ value: selected }}
                      form={{ setFieldValue: _.noop }}
                    />
                    <span className={styles.monthDiscount}>
                      {t('discount.percent', [upfrontPaymentMonth.discount])}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
          <section className={styles.totalSection}>
            <span>{t('total')}</span>
            <div className={styles.pricingWrapper}>
              <div className={styles.fullPricing}>{fullPricing}</div>
              <div className={styles.totalPricing}>{total}</div>
            </div>
          </section>
        </UiBlocker>
      </Dialog.Body>
      <Dialog.Footer className={styles.upfrontPaymentDialogFooter}>
        <BngButton variant={Variant.textButton} onClick={closeModal} disabled={loading}>
          {t('cancel')}
        </BngButton>
        <BngButton
          disabled={loading}
          onClick={async () => {
            setLoading(true);
            try {
              const startingProjection = selectedProjections[0].invoiceProjection;
              const monthApplied = upfrontPaymentMonths.find((month) => month.id === selectedMonth);

              await Api.Account.createInvoiceUpfrontPayment({
                accountId,
                projectionId: startingProjection.id,
                upfrontPaymentMonth: monthApplied,
              });
              UiMsg.ok(t('upfront.payment.created'));
              await onUpdate();
              closeModal();
            } catch (e) {
              console.error('Error on function createUpfrontPayment()', e);
              UiMsg.error(t('upfront.payment.create.error'));
              setLoading(false);
            }
          }}
        >
          {t('confirm')}
        </BngButton>
      </Dialog.Footer>
    </Dialog>
  );
}
